import { useCallback, useRef, useState } from "react";
import { getChat, streamChat } from "@/lib/assistantApi";

let localId = 0;
const nextId = () => `local-${++localId}`;

/** One ChatGPT conversation: messages, streaming state, and switching between saved chats. */
export default function useAssistantChat({ projectId, songId }) {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [songTitle, setSongTitle] = useState(null);
  const abortRef = useRef(null);

  const send = useCallback(async (text) => {
    const message = text.trim();
    if (!message || streaming) return;
    const replyId = nextId();
    setError(null);
    setStreaming(true);
    setMessages((m) => [...m, { id: nextId(), role: "user", content: message }, { id: replyId, role: "assistant", content: "", pending: true }]);
    const patchReply = (fn) => setMessages((m) => m.map((x) => (x.id === replyId ? fn(x) : x)));
    abortRef.current = new AbortController();
    try {
      await streamChat({
        message, sessionId, projectId, songId, signal: abortRef.current.signal,
        onEvent: (ev) => {
          if (ev.session_id) { setSessionId(ev.session_id); setSongTitle(ev.song_title || null); }
          if (ev.text) patchReply((x) => ({ ...x, content: x.content + ev.text }));
        },
      });
    } catch (e) {
      if (e.name !== "AbortError") setError(e.message);
    } finally {
      patchReply((x) => ({ ...x, pending: false }));
      setMessages((m) => m.filter((x) => x.role !== "assistant" || x.content || x.pending));
      setStreaming(false);
    }
  }, [streaming, sessionId, projectId, songId]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setSessionId(null); setMessages([]); setError(null); setSongTitle(null);
  }, []);

  const open = useCallback(async (id) => {
    abortRef.current?.abort();
    setError(null);
    const chat = await getChat(id);
    setSessionId(chat.id);
    setMessages(chat.messages || []);
    setSongTitle(chat.song_title || null);
  }, []);

  return { sessionId, messages, streaming, error, songTitle, send, reset, open };
}
